import React, { useEffect, useState } from "react";
import UserInfoForm from "./UserInfoForm";
import { getUser } from "../../services/UserInfo";
import { UserProfile, newUserProfile } from "../../data/userProfile";
import { getSocialMediaInfo } from "../../services/socialMediaPlatformInformation";
import { SocialMediaPlatformInfo } from "../../data/socialMediaData";
import { drawerTabs as defaultDrawerTabs } from "../../data/account/accountData";
import Header from "../common/Header";
import SessionListPage from "./SessionListPage";
import ResponseListPage from "./ResponseListPage";
import { getUserSessions } from "../../services/userSessionInformation";
import { getEventInfo } from "../../services/EventInformation"; // 🆕 Added
import { SessionInfo } from "../../data/SessionData";
import { useGlobal } from "../../utils/GlobalContext";
import { EventsListItem } from "../../data/eventInfo";
import { UserSessionEventData } from "../../data/UserSessionEventData";

export default function Account() {
  const [selectedMenuItem, setSelectedMenuItem] = useState("");
  const [userInfo, setUserInfo] = useState<UserProfile>(newUserProfile);
  const [socialMediaInfo, setSocialMediaInfo] = useState<SocialMediaPlatformInfo[]>([]);
  const [userSessionEvent, setUserSessionEvent] = useState<UserSessionEventData>();
  const [isSessionsLoading, setIsSessionsLoading] = useState(true);
  const { state } = useGlobal();
  const [userSessions, setUserSessions] = useState<SessionInfo[]>([]);
  const [events, setEvents] = useState<EventsListItem[]>([]);

  const CurrentId = state.UserProfile.Id.toString();

  useEffect(() => {
    if (state.UserProfile.RolesList.includes("User")) {
      const fetchSocialMediaInfo = async () => {
        try {
          const response = await getSocialMediaInfo();
          if (response && Array.isArray(response)) {
            setSocialMediaInfo(response);
          }
        } catch (err) {
          console.error("Failed to fetch social media information.", err);
        }
      };
      fetchSocialMediaInfo();
    }
  }, []);

  useEffect(() => {
    if (state.UserProfile.RolesList.includes("User") && state.UserProfile.IsExistingUser) {
      const fetchUserInfo = async () => {
        if (!CurrentId) return;
        try {
          const cinfo = await getUser(CurrentId);
          setUserInfo(cinfo);
        } catch (err) {
          console.error("Failed to fetch user information.", err);
        }
      };
      fetchUserInfo();
    }
  }, [CurrentId]);

  useEffect(() => {
    if (state.UserProfile.RolesList.includes("User")) {
      const fetchUserSessionsAndEvents = async () => {
        setIsSessionsLoading(true);
        try {
          const sessionResponse = await getUserSessions(CurrentId);
          setUserSessions(sessionResponse.UserSessions);

          const eventResponse = await getEventInfo(); // 🆕 Load full event info
          setEvents(eventResponse);
        } catch (err) {
          console.error("Failed to fetch user sessions or events.", err);
        }
        setIsSessionsLoading(false);
      };
      fetchUserSessionsAndEvents();
    }
  }, [CurrentId]);

  const handleMenuItemClick = (text: string) => {
    setSelectedMenuItem(text);
  };

  const handleUserInfoUpdate = async () => {
    const updatedUserInfo = await getUser(CurrentId);
    setUserInfo(updatedUserInfo);
  };

  const handleUpdateUserSession = async () => {
    setIsSessionsLoading(true);
    try {
      const response = await getUserSessions(CurrentId);
      if (response && Array.isArray(response)) {
        setUserSessionEvent(response);
      }
    } catch (err) {
      console.error("Failed to fetch user sessions.", err);
    }
    setIsSessionsLoading(false);
  };

  const renderComponent = () => {
    if (!state.UserProfile.IsExistingUser) {
      return (
          <UserInfoForm
              userInfo={userInfo}
              socialMediaInfo={socialMediaInfo}
              CurrentId={CurrentId}
              onUserInfoUpdate={handleUserInfoUpdate}
          />
      );
    }

    switch (selectedMenuItem) {
      case "My Sessions":
        return (
            <SessionListPage
                userSessions={userSessions}
                isLoading={isSessionsLoading}
                userId={CurrentId}
                events={events}
                onUserSessionUpdate={handleUpdateUserSession}
            />
        );

      case "Registered Events":
        return <ResponseListPage userId={CurrentId} events={events} />;

      default:
        return (
            <UserInfoForm
                userInfo={userInfo}
                socialMediaInfo={socialMediaInfo}
                CurrentId={CurrentId}
                onUserInfoUpdate={handleUserInfoUpdate}
            />
        );
    }
  };

  const drawerTabs = state.UserProfile.IsExistingUser
      ? defaultDrawerTabs
      : defaultDrawerTabs.filter((tab) => tab !== "My Sessions");

  return (
      <Header
          showDrawer={true}
          drawerTabs={drawerTabs}
          handleMenuItemClick={handleMenuItemClick}
          renderComponent={renderComponent}
      />
  );
}
